import React, { useState } from 'react'
import './MultiDatePicker.css'
import { FaCalendarAlt, FaChevronLeft, FaChevronRight } from 'react-icons/fa'

const MultiDatePicker = ({ selectedDates = [], onChange, placeholder = "Click to select dates" }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [currentMonth, setCurrentMonth] = useState(new Date())

  const formatDateKey = (date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const isDateSelected = (date) => {
    const dateKey = formatDateKey(date)
    return selectedDates.includes(dateKey)
  }

  const toggleDate = (date) => {
    const dateKey = formatDateKey(date)
    const isSelected = selectedDates.includes(dateKey)
    
    if (isSelected) {
      onChange(selectedDates.filter(d => d !== dateKey))
    } else {
      onChange([...selectedDates, dateKey].sort())
    }
  }

  const getDaysInMonth = (date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startingDayOfWeek = firstDay.getDay()

    const days = []
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null)
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day))
    }
    
    return days
  }

  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))
  }

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))
  }

  const formatDatesForDisplay = () => {
    if (selectedDates.length === 0) return ''
    return selectedDates
      .map(date => new Date(date + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }))
      .join(', ')
  }

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  const days = getDaysInMonth(currentMonth)
  const todayKey = formatDateKey(new Date())

  return (
    <div className="multi-date-picker-wrapper">
      <div className="date-input-wrapper">
        <input
          type="text"
          value={formatDatesForDisplay()}
          readOnly
          className={`date-display-input ${selectedDates.length > 0 ? 'has-dates' : ''}`}
          placeholder={placeholder}
          onClick={() => setIsOpen(!isOpen)}
        />
        <FaCalendarAlt 
          className="calendar-icon" 
          onClick={() => setIsOpen(!isOpen)}
        />
      </div>
      
      {isOpen && (
        <>
          <div className="calendar-overlay" onClick={() => setIsOpen(false)}></div>
          <div className="calendar-popup">
            <div className="calendar-header">
              <button type="button" className="calendar-nav-btn" onClick={goToPreviousMonth}>
                <FaChevronLeft />
              </button>
              <h3 className="calendar-month-year">
                {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
              </h3>
              <button type="button" className="calendar-nav-btn" onClick={goToNextMonth}>
                <FaChevronRight />
              </button>
            </div>
            
            <div className="calendar-weekdays">
              {dayNames.map(day => (
                <div key={day} className="calendar-weekday">{day}</div>
              ))}
            </div>
            
            <div className="calendar-days">
              {days.map((date, index) => {
                if (date === null) {
                  return <div key={`empty-${index}`} className="calendar-day empty"></div>
                }

                const dateKey = formatDateKey(date)
                const isSelected = isDateSelected(date)
                const isToday = dateKey === todayKey
                const isPast = dateKey < todayKey

                return (
                  <button
                    key={dateKey}
                    type="button"
                    className={`calendar-day ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''} ${isPast ? 'disabled' : ''}`}
                    onClick={() => !isPast && toggleDate(date)}
                    disabled={isPast}
                  >
                    {date.getDate()}
                  </button>
                )
              })}
            </div>
            
            <div className="calendar-footer">
              <button 
                type="button" 
                className="calendar-close-btn"
                onClick={() => setIsOpen(false)}
              >
                Done
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default MultiDatePicker

